import type { ReactNode } from "react"
import { Head } from "@inertiajs/react"

import { BrandLogo } from "@/components/ui/brand-logo"
import { t } from "@/lib/i18n"
import { cn } from "@/lib/utils"

const billItems = ["pastor", "aguachile", "jamaica"] as const

/**
 * The comanda, at rótulo scale, on the brand panel.
 *
 * It reuses `ui.landing.hero_bill.*` rather than declaring its own example bill.
 * There is one fictional table in this product's marketing and it should stay
 * one: duplicating four dish names across two locale files is fiction that
 * drifts, and a diner reading the landing and a manager signing in should be
 * looking at the same Mesa 12.
 *
 * HIERARCHY COMES FROM SIZE AND WEIGHT, NOT FROM OPACITY. On Comal a ticket
 * could fade its rows to 72% and stay readable, because Comal is nearly black.
 * On `--brand-solid` that same 72% measures 3.32:1 and fails: the panel is light
 * enough that transparency eats the contrast. So every row is Papel at 95% and
 * the ranking is carried by the type instead.
 */
function PanelComanda() {
  return (
    <div className="font-mono text-xs text-brand-surface-foreground/95 tabular-nums">
      <div className="flex items-baseline justify-between uppercase tracking-[0.14em]">
        <span>{t("ui.landing.hero_bill.table")}</span>
        <span>{t("ui.landing.hero_bill.live")}</span>
      </div>

      <div className="my-2 h-px bg-brand-surface-foreground/30" />

      {billItems.map((key) => (
        <div key={key} className="flex justify-between gap-3 py-0.5">
          <span className="truncate">
            {t(`ui.landing.hero_bill.items.${key}.quantity`)} ·{" "}
            {t(`ui.landing.hero_bill.items.${key}.name`)}
          </span>
          <span>{t(`ui.landing.hero_bill.items.${key}.amount`)}</span>
        </div>
      ))}

      <div className="my-2 h-px bg-brand-surface-foreground/30" />

      <div className="flex justify-between gap-3 text-sm font-medium text-brand-surface-foreground">
        <span>{t("ui.landing.hero_bill.totals.due_label")}</span>
        <span>{t("ui.landing.hero_bill.totals.due_value")}</span>
      </div>
    </div>
  )
}

type AuthShellProps = {
  /**
   * The browser tab title. REQUIRED, and required on purpose: all nine access
   * screens shipped without one and fell back to the layout's bare "Menny", so
   * three domains' worth of tabs were indistinguishable. Making it a prop of the
   * shell rather than a `<Head>` each page remembers to add means the type
   * checker catches the next one.
   */
  title: string
  /** The rótulo line. Short — it is set at display size and wraps badly past six words. */
  headline: string
  blurb?: string
  children: ReactNode
  className?: string
}

/**
 * The shell behind every sign-in, registration, password-reset and invite screen
 * across the three domains.
 *
 * WHY THESE SCREENS GET THE BRAND REGISTER. `BRAND.md` scopes the operational
 * register to surfaces used *during service* — quiet because someone is standing
 * up. Nobody is standing up mid-shift when they sign in: the threshold is seen
 * once a day, often before opening. It is the one product surface that can
 * announce, and the natural home for the deliberate brand-coloured moment §19
 * reserves.
 *
 * The panel is `--brand-solid`, not `--brand`. Papel on chile-500 measures
 * 3.72:1, so a panel at that step carries the headline (large text clears 3:1)
 * and nothing else — the comanda would be unreadable at any opacity. chile-600
 * measures 5.00:1 and carries all of it. Both the panel and its foreground are
 * theme-independent on purpose: this composition is fixed, like the Comal one it
 * replaced, so it must not resolve through `--background`.
 *
 * The graphic device is the comanda rather than a photograph, which is what
 * §21 requires while no real photography exists — and it is the better answer
 * anyway, since the ticket is the product rather than a stock image of one.
 */
export function AuthShell({ title, headline, blurb, children, className }: AuthShellProps) {
  // `grid-rows-[auto_1fr]` while stacked: a grid stretches its rows by default, so
  // the band would grow past its own content and leave a field of colour under the
  // blurb. The band takes what it needs; the form takes the rest.
  return (
    <>
      <Head title={title} />
      <div
        className={cn(
          "grid min-h-svh grid-rows-[auto_1fr] lg:grid-cols-[1.05fr_0.95fr] lg:grid-rows-none",
          className
        )}
      >
        {/* `justify-between` only once this is a real column. Stacked, it is a
            band across the top of a phone, and spreading two children to its far
            edges is how a greeting turns into half the viewport. Below `lg`
            everything sits together at the top and the band takes only the
            height it needs. */}
        <aside className="flex flex-col items-start gap-4 bg-brand-solid px-6 py-7 text-brand-surface-foreground md:px-10 lg:justify-between lg:gap-8 lg:py-12">
          {/* Knocked out, not painted: on a brand-coloured panel the booth should
              show the panel through it, the way the mark is drawn. */}
          <BrandLogo
            variant="lockup"
            booth="knockout"
            className="h-7 text-brand-surface-foreground"
          />

          <div>
            <p className="rotulo text-balance text-2xl sm:text-3xl lg:text-5xl">{headline}</p>
            {blurb ? (
              <p className="mt-3 max-w-[32ch] text-sm text-brand-surface-foreground/95">{blurb}</p>
            ) : null}
          </div>

          {/* `lg`, matching the grid's own breakpoint, NOT `sm`. Below `lg` the two
              columns stack, and a ticket inside a stacked panel pushes the form off
              the first screen — on a tablet you would scroll past a bill to reach
              the password field. The headline and the mark carry the register on
              their own until the panel is a real column. */}
          <div className="hidden w-full lg:block">
            <PanelComanda />
          </div>
        </aside>

        {/* Centred only as a column. Stacked, centring the form inside whatever
            row height is left over drops it a hundred-odd pixels below the band
            it is supposed to follow. */}
        <main className="flex flex-col justify-start bg-background px-6 py-10 md:px-10 lg:justify-center">
          <div className="mx-auto w-full max-w-sm">{children}</div>
        </main>
      </div>
    </>
  )
}
